import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Building2,
  Store,
  Users,
  Lightbulb,
  Database,
  TrendingDown,
  CheckSquare,
  Palette,
  Settings,
  FileText,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Package,
  Inbox,
  FlaskConical,
  AlertTriangle,
  Shield,
  Layers,
  LayoutGrid,
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { colors, getNeumorphicShadow } from '@/lib/designSystem';
import ThemeToggle from './ThemeToggle';
import OutletSwitcher from './OutletSwitcher';

const menuItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/organizations', icon: Building2, label: 'Organizations' },
  { path: '/outlets', icon: Store, label: 'Outlets' },
  { path: '/users', icon: Users, label: 'Users & Roles' },
  { path: '/intelligence', icon: Lightbulb, label: 'Intelligence Hub' },
  { path: '/governance', icon: Shield, label: 'Governance Feed' },
  { path: '/masters', icon: Database, label: 'Global Masters' },
  { path: '/ingredient-master', icon: Package, label: 'Ingredient Master', adminOnly: true },
  { path: '/ingredient-proposals', icon: Inbox, label: 'Ingredient Proposals', adminOnly: true },
  { path: '/vendor-mappings', icon: Database, label: 'Vendor Mappings', adminOnly: true },
  { path: '/vendor-categories', icon: Layers, label: 'Vendor Categories', adminOnly: true },
  { path: '/item-categories', icon: Layers, label: 'Item Categories', adminOnly: true },
  { path: '/vendor-analytics', icon: TrendingDown, label: 'Vendor Analytics' },
  { path: '/trial-analytics', icon: FlaskConical, label: 'Trial Analytics', adminOnly: true },
  { path: '/deletion-approvals', icon: CheckSquare, label: 'Deletion Approvals', adminOnly: true },
  { path: '/org-modules', icon: LayoutGrid, label: 'Applications', adminOnly: true },
  { path: '/themes', icon: Palette, label: 'Themes' },
  { path: '/configuration', icon: Settings, label: 'Configuration' },
  { path: '/audit-logs', icon: FileText, label: 'Audit Logs' },
];

const Sidebar = () => {
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(true);
  const { theme } = useTheme();
  const currentColors = colors[theme];

  return (
    <motion.aside
      initial={{ width: 240 }}
      animate={{ width: isExpanded ? 240 : 80 }}
      transition={{ duration: 0.3 }}
      className="hidden md:flex flex-col h-screen sticky top-0 transition-all"
      style={
        {
          background: currentColors.background,
          borderRight: `1px solid ${currentColors.border}`,
        }
      }
    >
      {/* Header */}
      <div className="p-6 flex items-center justify-between">
        <AnimatePresence mode="wait">
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="font-bold text-xl"
              style={{ color: currentColors.textPrimary }}
            >
              1OS Control
            </motion.div>
          )}
        </AnimatePresence>
        <motion.button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={
            {
              background: currentColors.cardBg,
              boxShadow: getNeumorphicShadow(theme),
            }
          }
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {isExpanded ? (
            <ChevronLeft size={20} style={{ color: currentColors.textSecondary }} />
          ) : (
            <ChevronRight size={20} style={{ color: currentColors.textSecondary }} />
          )}
        </motion.button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 space-y-2 overflow-y-auto">
        {menuItems
          .filter(item => !item.adminOnly || user?.is_org_admin)
          .map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className="group"
            style={{ textDecoration: 'none' }}
          >
            {({ isActive }) => (
              <motion.div
                className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all"
                style={
                  {
                    background: isActive ? currentColors.cardBg : 'transparent',
                    boxShadow: isActive ? getNeumorphicShadow(theme) : 'none',
                    color: isActive ? currentColors.accentBlue : currentColors.textSecondary,
                  }
                }
                whileHover={
                  {
                    x: 4,
                    transition: { duration: 0.2 },
                  }
                }
              >
                <item.icon size={20} />
                <AnimatePresence mode="wait">
                  {isExpanded && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.2 }}
                      className="font-medium text-sm whitespace-nowrap"
                    >
                      {item.label}
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Footer with Outlet Switcher, Theme Toggle & Logout */}
      <div className="p-4 flex flex-col gap-3">
        <OutletSwitcher />
        
        {/* Theme Toggle */}
        <div className="flex items-center justify-center">
          <ThemeToggle />
        </div>
        
        {/* Logout Button - Full Width, Prominent */}
        <LogoutButton isExpanded={isExpanded} />
      </div>

      {/* Version Info */}
      <AnimatePresence mode="wait">
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="p-4 text-center text-xs"
            style={{ color: currentColors.textSecondary }}
          >
            v1.1 - Multi-Tenant
          </motion.div>
        )}
      </AnimatePresence>
    </motion.aside>
  );
};

// Logout Button Component
const LogoutButton = ({ isExpanded }) => {
  const { logout } = useAuth();
  const { theme } = useTheme();
  const currentColors = colors[theme];
  const [showConfirm, setShowConfirm] = React.useState(false);

  const handleLogoutClick = () => {
    setShowConfirm(true);
  };

  const handleConfirmLogout = () => {
    setShowConfirm(false);
    logout();
  };

  const handleCancelLogout = () => {
    setShowConfirm(false);
  };

  return (
    <>
      <motion.button
        onClick={handleLogoutClick}
        className="w-full px-4 py-3 rounded-xl flex items-center gap-3 font-medium"
        style={{
          background: currentColors.cardBg,
          boxShadow: getNeumorphicShadow(theme),
          color: currentColors.accentRed,
        }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        title="Logout from Control Room"
      >
        <LogOut size={20} />
        <AnimatePresence mode="wait">
          {isExpanded && (
            <motion.span
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              className="text-sm"
            >
              Logout
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Logout Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-md mx-4"
          >
            <h3 className="text-xl font-bold mb-2" style={{ color: currentColors.textPrimary }}>
              Logout from Control Room?
            </h3>
            <p className="text-sm mb-6" style={{ color: currentColors.textSecondary }}>
              Are you sure you want to logout? You'll need to sign in again to access your dashboard.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleCancelLogout}
                className="flex-1 px-4 py-2 rounded-lg font-medium border"
                style={{
                  background: currentColors.cardBg,
                  borderColor: currentColors.border,
                  color: currentColors.textPrimary,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmLogout}
                className="flex-1 px-4 py-2 rounded-lg font-medium text-white"
                style={{
                  background: currentColors.accentRed,
                }}
              >
                Logout
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
